import { cn } from '@repo/ui'
import { CheckCheck, FileText } from 'lucide-react'

import { renderMessagePreview } from '@/lib/message-variables'
import { renderWhatsAppFormatting } from '@/lib/whatsapp-format'

export type PreviewMedia = {
  url: string
  mimeType?: string | null
  fileName?: string | null
}

function MediaBubble({ media }: { media: PreviewMedia }) {
  const mime = media.mimeType ?? ''
  if (mime.startsWith('image/')) {
    return (
      <img
        src={media.url}
        alt={media.fileName ?? 'Imagem'}
        className="mb-1 max-h-48 w-full rounded-lg object-cover"
      />
    )
  }
  if (mime.startsWith('video/')) {
    return (
      <video
        src={media.url}
        controls
        className="mb-1 max-h-48 w-full rounded-lg"
      />
    )
  }
  if (mime.startsWith('audio/')) {
    return <audio src={media.url} controls className="mb-1 w-full" />
  }
  return (
    <div className="mb-1 flex items-center gap-2 rounded-lg bg-black/5 px-2 py-1.5 dark:bg-white/10">
      <FileText className="h-4 w-4 shrink-0" />
      <span className="truncate text-xs">{media.fileName ?? 'Arquivo'}</span>
    </div>
  )
}

/**
 * Pré-visualização da mensagem numa bolha estilo WhatsApp, com as variáveis
 * substituídas por valores de exemplo e a formatação (*negrito*, _itálico_)
 * aplicada. Opcionalmente mostra a mídia anexada acima do texto.
 */
export function WhatsAppPreview({
  text,
  media,
  className,
  extraSamples,
  headerText,
  footer,
  buttons,
}: {
  text: string
  media?: PreviewMedia | null
  className?: string
  /** Amostras adicionais (ex.: campos personalizados) para o preview. */
  extraSamples?: Record<string, string>
  /** Cabeçalho de texto (renderizado em negrito acima do corpo). */
  headerText?: string | null
  /** Rodapé (texto esmaecido abaixo do corpo). */
  footer?: string | null
  /**
   * Botões do template, exibidos abaixo da bolha. Botões com `url` (tipo link)
   * viram âncoras clicáveis; os demais são apenas rótulos, como no WhatsApp.
   */
  buttons?: Array<{ label: string; url?: string | null }> | null
}) {
  const hasText = text.trim().length > 0
  const html = renderWhatsAppFormatting(
    renderMessagePreview(text, extraSamples)
  )
  const headerHtml = headerText?.trim()
    ? renderWhatsAppFormatting(renderMessagePreview(headerText, extraSamples))
    : null

  return (
    <div
      className={cn(
        'rounded-lg border bg-[#efe7dd] p-3 dark:bg-[#0b141a]',
        className
      )}
    >
      <div className="text-foreground ml-auto w-fit max-w-[85%] rounded-2xl rounded-tr-sm bg-[#d9fdd3] px-3 py-1.5 text-sm shadow-sm dark:bg-[#005c4b]">
        {media ? <MediaBubble media={media} /> : null}
        {headerHtml ? (
          <div
            className="mb-0.5 font-semibold break-words"
            dangerouslySetInnerHTML={{ __html: headerHtml }}
          />
        ) : null}
        {hasText ? (
          <div
            className="break-words [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : media || headerHtml ? null : (
          <span className="text-muted-foreground italic">
            Pré-visualização da mensagem…
          </span>
        )}
        {footer?.trim() ? (
          <div className="text-muted-foreground mt-1 text-xs">
            {footer.trim()}
          </div>
        ) : null}
        <div className="text-2xs text-muted-foreground mt-0.5 flex items-center justify-end gap-1">
          <span>14:32</span>
          <CheckCheck className="text-info h-3 w-3" />
        </div>
        {buttons?.length ? (
          <div className="-mx-3 mt-1.5 border-t border-black/10 dark:border-white/10">
            {buttons.map((button, index) =>
              button.url ? (
                <a
                  key={index}
                  href={button.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-info block border-b border-black/5 py-1.5 text-center text-sm font-medium underline-offset-2 last:border-b-0 hover:underline dark:border-white/5"
                >
                  {button.label}
                </a>
              ) : (
                <div
                  key={index}
                  className="text-info border-b border-black/5 py-1.5 text-center text-sm font-medium last:border-b-0 dark:border-white/5"
                >
                  {button.label}
                </div>
              )
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}
